import { BtnClasses, Button, Icon, PanelLayout } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import {
  For,
  Match,
  Show,
  Switch,
  type Accessor,
  type Component,
} from 'solid-js';
import type {
  CustomEntityPropertySetsInput,
  ObjectPropertiesSourceUnits,
} from './object-properties.types';
import type { FormattingSettings } from '@trimble/types';
import { ObjectPropertiesPanelCapability } from '@trimble/web3d-capabilities';
import { ObjectPropertiesPanelActions } from './ObjectPropertiesPanelActions';
import { PropertySetItem } from './PropertySetItem';
import { useDeferredFavorites } from './useDeferredFavorites';
import { useExpandedPropertySets } from './useExpandedPropertySets';
import { useObjectPropertiesPanelModel } from './useObjectPropertiesPanelModel';

export interface ObjectPropertiesPanelProps {
  capability: ObjectPropertiesPanelCapability;
  /**
   * Project formatting settings used to render unit-typed property values
   * (Length / Area / Volume / Angle / Double). Pass the accessor from
   * `useProjectSettings().formattingSettings` so display values stay in sync
   * when the user changes units in project settings.
   */
  formattingSettings: Accessor<FormattingSettings>;
  /**
   * Optional override of the source units assumed for raw numeric values.
   * Defaults match CDM/trimbim conventions (mm / m² / m³ / deg).
   */
  sourceUnits?: ObjectPropertiesSourceUnits;
  sortAlphabetically?: boolean;
  refineNames?: boolean;
  expandedRows?: readonly string[];
  favoritePropertyKeys?: readonly string[];
  /** When `false`, custom-property mutation menu actions are gated. `undefined` = policy still loading. */
  customPropertiesMutationAllowed?: boolean;
  /** When `true`, shows a loading indicator at the top of the panel while properties or custom properties are loading. */
  isLoading?: boolean;
  onSortAlphabeticallyChanged?(sortAlphabetically: boolean): void;
  onRefineNamesChanged?(refineNames: boolean): void;
  onExpandedRowsChange(rows: string[]): void;
  /** Persists the favorite property keys immediately (backend save only). */
  onFavoritePropertyKeysChange(keys: string[]): void;
  onAddProperties?(): void;
  onManagePropertySetLibraries?(): void;
  customEntityPropertySets?: readonly CustomEntityPropertySetsInput[];
  onClose(): void;
}

export const ObjectPropertiesPanel: Component<ObjectPropertiesPanelProps> = (
  props
) => {
  const favorites = useDeferredFavorites({
    favoritePropertyKeys: () => props.favoritePropertyKeys,
    data: () => props.capability.data(),
    onFavoritePropertyKeysChange: (keys) =>
      props.onFavoritePropertyKeysChange(keys),
  });

  const model = useObjectPropertiesPanelModel({
    capability: () => props.capability,
    formattingSettings: () => props.formattingSettings(),
    sourceUnits: () => props.sourceUnits,
    sortAlphabetically: () => props.sortAlphabetically,
    refineNames: () => props.refineNames,
    customEntityPropertySets: () => props.customEntityPropertySets,
    groupedFavoriteKeys: favorites.groupedFavoriteKeys,
  });

  const expanded = useExpandedPropertySets({
    expandedRows: () => props.expandedRows,
    onExpandedRowsChange: (rows) => props.onExpandedRowsChange(rows),
    regularSetKeys: () => model.panelData().regularSetKeys,
    favoriteSetKeys: () => model.panelData().favoriteSetKeys,
  });

  const shouldShowRegularSectionHeader = (): boolean =>
    model.panelData().regularSets.length > 0;

  return (
    <PanelLayout
      customTitle={
        model.isMultiSelection() ? (
          <span class="pl-2 inline-block leading-tight text-xs font-bold truncate uppercase">
            {t('objectProperties.multiSelectionTitle', {
              count: model.selectionCount(),
            })}
          </span>
        ) : (
          <span class="pl-2 inline-block leading-tight text-[1.375rem] font-semibold truncate">
            {model.data()?.title ?? t('objectProperties.defaultTitle')}
          </span>
        )
      }
      titleDataCy="object-properties-tab"
      rightActions={
        <ObjectPropertiesPanelActions
          selectionCount={model.selectionCount()}
          sortAlphabetically={props.sortAlphabetically}
          refineNames={props.refineNames}
          customPropertiesMutationAllowed={
            props.customPropertiesMutationAllowed
          }
          onSortAlphabeticallyChanged={props.onSortAlphabeticallyChanged}
          onRefineNamesChanged={props.onRefineNamesChanged}
          onAddProperties={props.onAddProperties}
          onManagePropertySetLibraries={props.onManagePropertySetLibraries}
          onClose={props.onClose}
        />
      }
      content={
        <Show
          when={model.selectionCount() > 0}
          fallback={
            <div class="flex flex-col items-center justify-center h-full p-4">
              <Icon
                icon="tc-icon-3D-group"
                size="icon-xlarge"
                class="text-trimble_gray opacity-30"
              />
              <span class="text-center text-muted p-4">
                {t('objectProperties.emptySelection')}
              </span>
            </div>
          }
        >
          <div class="relative flex flex-col h-full">
            <div
              class={`flex-1 overflow-y-auto ${favorites.favoritesListOutdated() ? 'pb-16' : ''}`}
            >
              <Show when={props.isLoading}>
                <div
                  class="flex items-center justify-center gap-2 p-3 text-muted"
                  data-testid="object-properties-loading"
                >
                  <i class="icon-font tc-icon-spinner-dark" />
                  <div class="text-meta ml-1">
                    {t('objectProperties.loading')}
                  </div>
                </div>
              </Show>
              <Show when={model.panelData().favoriteSets.length > 0}>
                <div
                  class="flex p-2 pl-4 text-[#8c8b96]"
                  data-testid="object-properties-favorites-title"
                >
                  <span class="flex flex-1 items-center text-[0.687rem] font-bold uppercase">
                    {t('objectProperties.favoritesTitle')}
                  </span>
                  <Show when={model.panelData().favoriteSetKeys.length > 0}>
                    <Button
                      color={BtnClasses.TERTIARY}
                      size="small"
                      type="solid"
                      class="!text-[#8c8b96] hover:!text-[#363545]"
                      content={
                        expanded.favoriteAllExpanded()
                          ? t('objectProperties.collapseAll')
                          : t('objectProperties.expandAll')
                      }
                      onClick={() => {
                        expanded.toggleFavoriteExpandCollapseAll();
                      }}
                    />
                  </Show>
                </div>

                <For each={model.panelData().favoriteSets}>
                  {(pset) => {
                    const showAll = (): boolean =>
                      expanded.showAllInFavoriteSetKeys().has(pset.key);

                    return (
                      <PropertySetItem
                        testId={`favorite-${pset.key}`}
                        name={pset.name}
                        expanded={expanded.expandedKeys().has(pset.key)}
                        onToggle={() => expanded.toggleKey(pset.key)}
                        properties={favorites.withFavoriteState(
                          pset.favoriteProperties
                        )}
                        secondaryProperties={
                          showAll()
                            ? favorites.withFavoriteState(
                                pset.nonFavoriteProperties
                              )
                            : undefined
                        }
                        onToggleFavorite={favorites.toggleFavoriteKey}
                        secondaryPropertiesExpanded={
                          pset.nonFavoriteProperties.length > 0
                            ? showAll()
                            : undefined
                        }
                        onSecondaryPropertiesToggle={
                          pset.nonFavoriteProperties.length > 0
                            ? () =>
                                expanded.toggleShowAllForFavoriteSet(pset.key)
                            : undefined
                        }
                      />
                    );
                  }}
                </For>
              </Show>

              <Show when={shouldShowRegularSectionHeader()}>
                <div class="flex p-2 pl-4 text-[#8c8b96]">
                  <Switch>
                    <Match when={model.isMultiSelection()}>
                      <span class="flex flex-1 items-center text-[0.687rem] font-bold uppercase">
                        {t('objectProperties.multiSelectionListTitle')}
                      </span>
                    </Match>
                    <Match when={!model.isMultiSelection()}>
                      <span class="flex flex-1 items-center text-[0.687rem] font-bold uppercase">
                        {t('objectProperties.listTitle')}
                      </span>
                    </Match>
                  </Switch>
                  <Show when={model.panelData().regularSetKeys.length > 0}>
                    <Button
                      color={BtnClasses.TERTIARY}
                      size="small"
                      type="solid"
                      class="!text-[#8c8b96] hover:!text-[#363545]"
                      content={
                        expanded.allExpanded()
                          ? t('objectProperties.collapseAll')
                          : t('objectProperties.expandAll')
                      }
                      onClick={() => {
                        expanded.toggleExpandCollapseAll();
                      }}
                    />
                  </Show>
                </div>
              </Show>

              <Show when={model.isMultiSelection() && !model.hasSharedProps()}>
                <div class="flex flex-1 flex-col items-center justify-center p-4">
                  <Icon
                    icon="tc-icon-metadata"
                    size="icon-xlarge"
                    class="text-trimble_gray opacity-30"
                  />
                  <span class="text-center text-muted p-4">
                    {t('objectProperties.noCommonProperties')}
                  </span>
                </div>
              </Show>

              <For each={model.panelData().regularSets}>
                {(pset) => {
                  return (
                    <PropertySetItem
                      testId={pset.key}
                      name={pset.name}
                      expanded={expanded.expandedKeys().has(pset.key)}
                      onToggle={() => expanded.toggleKey(pset.key)}
                      properties={favorites.withFavoriteState(pset.properties)}
                      onToggleFavorite={favorites.toggleFavoriteKey}
                    />
                  );
                }}
              </For>
            </div>
            <Show when={favorites.favoritesListOutdated()}>
              <div class="absolute bottom-4 left-1/2 -translate-x-1/2">
                <Button
                  color={BtnClasses.PRIMARY}
                  size="small"
                  type="solid"
                  content={t('objectProperties.refreshList')}
                  data-cy="object-properties-refresh-list"
                  onClick={favorites.refreshFavoritesList}
                />
              </div>
            </Show>
          </div>
        </Show>
      }
    />
  );
};
